import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, EmptyState, Popover } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatDateTime, formatRelative } from '@/lib/format';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

const KEY = ['notifications'];

/** Bell in the top bar: leave approvals, task assignments, automation notices. */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: KEY,
    queryFn: () => api<{ items: Notification[]; unread: number }>('/admin/notifications'),
    refetchInterval: 60_000,
  });
  const markRead = async (ids?: string[]) => {
    await api('/admin/notifications/read', { method: 'POST', body: ids ? { ids } : {} });
    await qc.invalidateQueries({ queryKey: KEY });
  };
  const unread = list.data?.unread ?? 0;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      className="w-96"
      trigger={
        <Button variant="ghost" size="icon" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} className="relative">
          <Bell />
          {unread ? (
            <span className="absolute right-1 top-1 flex min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-4 text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </Button>
      }
    >
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-semibold">Notifications</span>
        {unread ? (
          <Button size="sm" variant="ghost" onClick={() => void markRead()}>
            <CheckCheck /> Mark all read
          </Button>
        ) : null}
      </div>
      <div className="max-h-[min(24rem,calc(var(--radix-popover-content-available-height)-3rem))] overflow-y-auto overscroll-contain">
        {list.data?.items.length ? (
          <ul className="divide-y">
            {list.data.items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={cn('flex w-full gap-3 px-3 py-2.5 text-left hover:bg-surface-hover', !n.readAt && 'bg-accent-soft/40')}
                  onClick={() => {
                    if (!n.readAt) void markRead([n.id]);
                    setOpen(false);
                    if (n.link) navigate(n.link);
                  }}
                >
                  <span aria-hidden className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-accent')} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{n.title}</span>
                    {n.body ? <span className="block truncate text-xs text-muted">{n.body}</span> : null}
                    <time className="mt-0.5 block text-xs text-subtle" dateTime={n.createdAt} title={formatDateTime(n.createdAt)}>
                      {formatRelative(n.createdAt)}
                    </time>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon={<Bell />} title="You're all caught up" className="py-10" />
        )}
      </div>
    </Popover>
  );
}
